import {Fragment, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {matchEventTime} from "@/utils/matchEvents";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
	ResponsiveContainer,
	BarChart,
	Bar,
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as ChartTooltip,
} from "recharts";
import HomeIcon from "@mui/icons-material/Home";
import FlightIcon from "@mui/icons-material/Flight";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import HandshakeIcon from "@mui/icons-material/Handshake";
import VLFCard from "@/components/Card";
import VLFSelect from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import {PlayerAPI} from "@/api/player";
import {technicalGroups, ratioLabels} from "@/utils/computePlayerStats";
import "./index.scss";
import {COLORS} from "@/style/utils";

const qualityLabels = {
	complete: "Completi",
	partial: "Parziali",
	missing: "Mancanti",
	check: "Da verificare",
};
const display = (value, decimals = 0) =>
	value == null
		? "—"
		: Number(value).toLocaleString("it-IT", {
				maximumFractionDigits: decimals,
				minimumFractionDigits: decimals,
			});

function Metric({row, name, label, children}) {
	const metric = row.statistics?.metrics?.[name];
	const value = metric
		? metric.conflict
			? null
			: metric.value
		: row.statistics?.values?.[name];
	const description = metric?.conflict
		? "Fonti in conflitto: verifica nella partita"
		: value == null
			? "Dato non disponibile"
			: metric?.source === "events"
				? "Calcolato dagli eventi"
				: "Dato registrato nella partita";
	return (
		<Tooltip title={label + ": " + description}>
			<span className="player-match-statistics__metric">
				{children}
				{display(value, name === "rating" ? 1 : 0)}
			</span>
		</Tooltip>
	);
}

function TechnicalDetails({data}) {
	const [showMissing, setShowMissing] = useState(false);
	const totals = data?.totals || {};
	const fields = technicalGroups
		.filter(
			(group) =>
				!group.goalkeeper ||
				data?.goalkeeper ||
				group.fields.some(([key]) => totals[key]?.knownMatches),
		)
		.flatMap((group) =>
			group.fields.map(([key, label]) => ({key, label, group: group.title})),
		);
	const available = (key) =>
		totals[key]?.value != null ||
		totals[key]?.per90 != null ||
		data?.ratios?.[key]?.percentage != null;
	const count = fields.filter(({key}) => available(key)).length;
	const rows = fields.filter(({key}) => showMissing || available(key));
	return (
		<details className="player-match-statistics__technical">
			<summary>
				Dettaglio tecnico <span>{count} metriche disponibili</span>
			</summary>
			<div className="player-match-statistics__technical-toolbar">
				<Tooltip title="Stesso periodo, rosa e competizione del riepilogo. I totali sommano soltanto le gare con il dato registrato. Le metriche possono sovrapporsi e non vengono sommate tra loro. Le percentuali usano le somme delle gare con entrambi i dati; per i portieri non sono dedotte dai soli gol subiti.">
					<span
						tabIndex={0}
						className="player-match-statistics__technical-help"
					>
						Come leggere i dati ⓘ
					</span>
				</Tooltip>
				<button
					type="button"
					className="player-match-statistics__missing-toggle"
					aria-pressed={showMissing}
					onClick={() => setShowMissing(!showMissing)}
				>
					{showMissing
						? "Nascondi dati non raccolti"
						: `Mostra dati non raccolti (${fields.length - count})`}
				</button>
			</div>
			{rows.length ? (
				<div className="player-match-statistics__table-wrapper player-match-statistics__technical-table">
					<table>
						<caption className="visually-hidden">
							Statistiche tecniche della selezione di gare
						</caption>
						<thead>
							<tr>
								<th scope="col">Dato</th>
								<th scope="col">Totale</th>
								<th scope="col">
									<Tooltip title="Solo gare con dato e minuti sportivi noti, maggiori di zero. Il campione può cambiare per ogni metrica.">
										<span tabIndex={0}>Per 90′ ⓘ</span>
									</Tooltip>
								</th>
								<th scope="col">Copertura</th>
							</tr>
						</thead>
						<tbody>
							{rows.map(({key, label, group}) => {
								const metric = totals[key];
								const ratio = data?.ratios?.[key];
								const coverage = metric?.knownMatches
									? `${metric.knownMatches}/${metric.eligibleMatches} gare`
									: "Dato non raccolto";
								const per90Coverage = metric?.per90KnownMatches
									? `${metric.per90KnownMatches} gare · ${metric.per90Minutes} minuti sportivi`
									: "Minuti o dato non disponibili";
								return (
									<tr key={key}>
										<th scope="row">
											<Tooltip title={group}>
												<span tabIndex={0}>{label}</span>
											</Tooltip>
										</th>
										<td>
											<strong>
												{display(
													metric?.value,
													["expectedGoals", "expectedAssists"].includes(key)
														? 2
														: 0,
												)}
											</strong>
											{ratio?.percentage != null && (
												<Tooltip
													title={`${ratioLabels[key]}: ${display(ratio.successful)}/${display(ratio.attempted)} su ${ratio.knownMatches}/${ratio.eligibleMatches} gare con entrambi i dati.`}
												>
													<span
														tabIndex={0}
														className="player-match-statistics__percentage"
													>
														{display(ratio.percentage, 1)}%
													</span>
												</Tooltip>
											)}
										</td>
										<td>
											<Tooltip title={per90Coverage}>
												<span tabIndex={0}>{display(metric?.per90, 2)}</span>
											</Tooltip>
										</td>
										<td>{coverage}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			) : (
				<p className="player-match-statistics__note">
					Nessun dato tecnico registrato per questa selezione.
				</p>
			)}
		</details>
	);
}

function profileNumber(row, key) {
	const metric = row.statistics?.metrics?.[key];
	const value = metric
		? metric.conflict
			? null
			: metric.value
		: row.statistics?.values?.[key];
	return value == null || !Number.isFinite(Number(value))
		? null
		: Number(value);
}
function MatchChartTooltip({active, payload, metric}) {
	const row = payload?.[0]?.payload;
	if (!active || !row) return null;
	return (
		<div className="player-match-statistics__chart-tooltip">
			<strong>{row.fixture}</strong>
			<span>
				{row.dateLabel} · {row.status}
			</span>
			<span>
				{metric === "minutes" ? "Minuti" : "Voto"}:{" "}
				{display(row[metric], metric === "rating" ? 1 : 0)}
			</span>
			{row[metric] == null && <small>Dato non disponibile</small>}
		</div>
	);
}
function ProfileCharts({matches}) {
	const rows = [...matches]
		.sort((a, b) => new Date(a.date) - new Date(b.date))
		.map((row) => ({
			id: row.matchId,
			day: moment(row.date).format("DD/MM"),
			dateLabel: moment(row.date).format("DD/MM/YYYY"),
			fixture: `${row.homeTeam} Vs ${row.awayTeam}`,
			status: row.status,
			minutes: profileNumber(row, "minutes"),
			rating: row.played ? profileNumber(row, "rating") : null,
		}));
	const minutesKnown = rows.filter((row) => row.minutes != null).length;
	const ratingsKnown = rows.filter((row) => row.rating != null).length;
	return (
		<div className="player-match-statistics__charts">
			<section>
				<h4>
					Andamento minuti <small>{minutesKnown} gare con minutaggio</small>
				</h4>
				{minutesKnown ? (
					<ResponsiveContainer width="100%" height={190}>
						<BarChart
							data={rows}
							margin={{top: 10, right: 12, left: -18, bottom: 0}}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								vertical={false}
								stroke={COLORS.border}
							/>
							<XAxis dataKey="day" tick={{fontSize: 10}} minTickGap={24} />
							<YAxis allowDecimals={false} tick={{fontSize: 10}} />
							<ChartTooltip
								content={<MatchChartTooltip metric="minutes" />}
								filterNull={false}
							/>
							<Bar
								dataKey="minutes"
								name="Minuti"
								fill={COLORS.primary}
								radius={[3, 3, 0, 0]}
								maxBarSize={32}
								isAnimationActive={false}
							/>
						</BarChart>
					</ResponsiveContainer>
				) : (
					<p>Nessun minutaggio disponibile nel periodo.</p>
				)}
			</section>
			<section>
				<h4>
					Andamento voto <small>{ratingsKnown} voti disponibili</small>
				</h4>
				{ratingsKnown ? (
					<ResponsiveContainer width="100%" height={190}>
						<LineChart
							data={rows}
							margin={{top: 10, right: 12, left: -18, bottom: 0}}
						>
							<CartesianGrid
								strokeDasharray="3 3"
								vertical={false}
								stroke={COLORS.border}
							/>
							<XAxis dataKey="day" tick={{fontSize: 10}} minTickGap={24} />
							<YAxis
								domain={[1, 10]}
								ticks={[1, 4, 7, 10]}
								tick={{fontSize: 10}}
							/>
							<ChartTooltip
								content={<MatchChartTooltip metric="rating" />}
								filterNull={false}
							/>
							<Line
								dataKey="rating"
								name="Voto"
								stroke={COLORS.primary}
								strokeWidth={2}
								dot={{r: 4}}
								connectNulls={false}
								isAnimationActive={false}
							/>
						</LineChart>
					</ResponsiveContainer>
				) : (
					<p>Nessun voto disponibile nel periodo.</p>
				)}
			</section>
			<p className="player-match-statistics__note">
				Il voto è quello della gara. I dati mancanti restano vuoti e non
				diventano zero; le gare future sono escluse.
			</p>
		</div>
	);
}

const profileEventLabels = {
	goal: "Gol",
	own_goal: "Autogol",
	penalty_scored: "Rigore segnato",
	penalty_missed: "Rigore sbagliato",
	injury_exit: "Uscita per infortunio",
	yellow_card: "Cartellino giallo",
	double_yellow_card: "Doppio giallo",
	red_card: "Cartellino rosso",
};
function MatchDetails({row, playerId}) {
	const stats = row.statistics;
	const involvement = (event) => {
		if (event.assistPlayerId === playerId) return "Assist";
		if (event.eventType === "substitution")
			return `${event.substitutePlayerId === playerId ? "Esce" : "Entra"}${event.substitutionReason === "injury" ? " · sostituzione per infortunio" : " · sostituzione"}`;
		return profileEventLabels[event.eventType] || event.eventType;
	};
	return (
		<div className="player-match-statistics__game-detail">
			<strong>Dettaglio gara · {row.status}</strong>
			{row.warning && <p role="status">{row.warning}</p>}
			<p>
				Voto di gara: {display(profileNumber(row, "rating"), 1)} · Minuti:{" "}
				{display(profileNumber(row, "minutes"))}
			</p>
			<h4>Eventi del giocatore</h4>
			{row.events?.length ? (
				<ul>
					{row.events.map((event) => (
						<li key={event.id}>
							<strong>{matchEventTime(event)}</strong> · {involvement(event)}
							{event.notes && <small>{event.notes}</small>}
						</li>
					))}
				</ul>
			) : (
				<p>
					Nessun evento registrato per questo giocatore. L’assenza di eventi non
					certifica la completezza dei dati.
				</p>
			)}
			<div className="player-match-statistics__technical-grid">
				{technicalGroups
					.filter((group) => !group.goalkeeper || stats?.goalkeeper)
					.map((group) => {
						const fields = group.fields.filter(
							([key]) =>
								profileNumber(row, key) != null ||
								stats?.metrics?.[key]?.conflict,
						);
						if (!fields.length) return null;
						return (
							<section
								key={group.title}
								className="player-match-statistics__technical-group"
							>
								<h4>{group.title}</h4>
								<dl>
									{fields.map(([key, label]) => (
										<div
											key={key}
											className="player-match-statistics__technical-row"
										>
											<dt>{label}</dt>
											<dd>
												<strong>
													{display(
														profileNumber(row, key),
														["expectedGoals", "expectedAssists"].includes(key)
															? 2
															: 0,
													)}
												</strong>
												{stats?.metrics?.[key]?.conflict && (
													<small>
														Fonti in conflitto: verifica nella partita
													</small>
												)}
											</dd>
										</div>
									))}
								</dl>
							</section>
						);
					})}
			</div>
			{!stats && (
				<p>Nessuna statistica individuale disponibile per questa gara.</p>
			)}
			<h4>Valutazione di gara</h4>
			<p className="player-match-statistics__text">
				Punti di forza: {stats?.values?.strengths || "—"}
			</p>
			<p className="player-match-statistics__text">
				Da migliorare: {stats?.values?.improvements || "—"}
			</p>
			<Link to={"/matches/" + row.matchId}>
				Apri la partita per consultare o modificare i dati
			</Link>
		</div>
	);
}

export default function PlayerMatchStatistics({
	playerId,
	seasonId,
	summaryOnly = false,
	showTitle = true,
}) {
	const [data, setData] = useState(null);
	const [expandedMatch, setExpandedMatch] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [roster, setRoster] = useState(null);
	const [competition, setCompetition] = useState(null);
	const [from, setFrom] = useState(null);
	const [to, setTo] = useState(null);
	const [revision, setRevision] = useState(0);
	const invalidDates = from && to && moment(from).isAfter(to, "day");
	useEffect(() => {
		let active = true;
		if (!playerId || !seasonId || invalidDates) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setError(null);
		PlayerAPI.matchStatistics(playerId, {
			seasonId,
			rosterId: roster?.value,
			competitionSeasonId: competition?.value,
			fromDate: from ? moment(from).format("YYYY-MM-DD") : undefined,
			toDate: to ? moment(to).format("YYYY-MM-DD") : undefined,
		})
			.then((response) => {
				if (active) setData(response || null);
			})
			.catch(() => {
				if (active) {
					setError("Impossibile caricare le statistiche delle partite.");
					setData(null);
				}
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [
		playerId,
		seasonId,
		roster?.value,
		competition?.value,
		from,
		to,
		revision,
		invalidDates,
	]);
	const visibleMatches = (data?.matches || []).filter((row) => !row.upcoming);
	// Il totale comprende tutte le gare trascorse della selezione, anche senza convocazione.
	const completeTotal = (values) =>
		!data || values.some((value) => value == null)
			? null
			: values.reduce((total, value) => total + Number(value), 0);
	const availableMinutes = completeTotal(
		visibleMatches.map((row) => row.sportingMinutes),
	);
	const teamGoals = (conceded = false) =>
		completeTotal(
			visibleMatches.map((row) => {
				if (row.matchSide !== "home" && row.matchSide !== "away") return null;
				const home = row.matchSide === "home";
				return home !== conceded ? row.homeGoals : row.awayGoals;
			}),
		);
	const denominators = {
		minutes: {
			value: availableMinutes,
			description:
				"Minuti sportivi complessivi delle gare trascorse, incluse quelle senza convocazione; recupero escluso",
		},
		goals: {
			value: teamGoals(),
			description:
				"Gol complessivi della squadra nelle gare trascorse selezionate",
		},
		assists: {
			value: teamGoals(),
			description:
				"Gol complessivi della squadra nelle gare trascorse selezionate",
		},
		goalsConceded: {
			value: teamGoals(true),
			description:
				"Gol complessivamente subiti dalla rosa nelle gare trascorse selezionate",
		},
		rating: {
			value: 10,
			description: "Voto medio di gara sulla scala da 1 a 10",
		},
	};
	const employment = [
		["Titolare", "Titolarità"],
		["Subentrato", "Subentri"],
		["Panchina", "Panchine senza ingresso"],
		["Non convocato", "Non convocazioni"],
	].map(([status, label]) => ({
		label,
		value: visibleMatches.filter((row) => row.status === status).length,
	}));
	const otherEmployment =
		visibleMatches.length -
		employment.reduce((sum, item) => sum + item.value, 0);
	const minuteShare =
		availableMinutes > 0 && data?.totals?.minutes?.value != null
			? (data.totals.minutes.value * 100) / availableMinutes
			: null;
	const contributionGames = visibleMatches.filter((row) => row.inLineup);
	const contributionKnown = contributionGames.filter(
		(row) =>
			profileNumber(row, "goals") != null &&
			profileNumber(row, "assists") != null,
	);
	const contribution = contributionKnown.length
		? contributionKnown.reduce(
				(sum, row) =>
					sum + profileNumber(row, "goals") + profileNumber(row, "assists"),
				0,
			)
		: null;
	const contributionComplete =
		contributionKnown.length === contributionGames.length &&
		contribution != null;
	const contributionShare =
		contributionComplete && teamGoals() > 0
			? (contribution * 100) / teamGoals()
			: null;
	const keys = data?.goalkeeper
		? [
				["minutes", "Minuti"],
				["goalsConceded", "Gol subiti"],
				["saves", "Parate"],
				["rating", "Voto medio"],
			]
		: [
				["minutes", "Minuti"],
				["goals", "Gol"],
				["assists", "Assist"],
				["rating", "Voto medio"],
			];
	const metricTile = ([key, label]) => {
		const metric = data?.totals?.[key];
		return (
			<div className="player-match-statistics__tile" key={key}>
				<strong>
					{display(metric?.value, key === "rating" ? 1 : 0)}
					{denominators[key] && (
						<Tooltip
							title={
								denominators[key].description +
								(denominators[key].value == null
									? ": totale non disponibile per dati mancanti"
									: "")
							}
						>
							<span className="player-match-statistics__denominator">
								/{display(denominators[key].value)}
							</span>
						</Tooltip>
					)}
				</strong>
				<span>{label}</span>
				<small>
					{key === "rating"
						? `${metric?.knownMatches ?? 0} voti disponibili`
						: metric?.eligibleMatches
							? `Dato su ${metric.knownMatches}/${metric.eligibleMatches} gare in distinta`
							: "Nessun dato disponibile"}
				</small>
			</div>
		);
	};
	const summary = (
		<>
			<div className="player-match-statistics__heading">
				<span>Stagione {data?.seasonName || "selezionata"}</span>
				{!summaryOnly && (
					<Tooltip title="Aggiorna statistiche">
						<IconButton
							size="small"
							onClick={() => setRevision((v) => v + 1)}
							disabled={loading}
						>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
			</div>
			<div className="player-match-statistics__summary">
				<div className="player-match-statistics__tile">
					<strong>
						{display(data?.playedMatches)}
						<span className="player-match-statistics__denominator">
							/{display(data?.pastMatches)}
						</span>
					</strong>
					<span>Gare giocate</span>
					<small>
						{data?.lineupMatches ?? "—"} in distinta ·{" "}
						{data?.pastMatches ?? "—"} gare trascorse
					</small>
				</div>
				{keys.filter(([key]) => key !== "rating").map(metricTile)}
				<Tooltip title="Gol più assist del giocatore / gol complessivi della squadra nelle gare trascorse selezionate. La percentuale è disponibile solo con dati individuali completi e totale squadra noto e maggiore di zero.">
					<div className="player-match-statistics__tile">
						<strong>
							{display(contribution)}
							<span className="player-match-statistics__denominator">
								/{display(teamGoals())}
							</span>
						</strong>
						<span>Partecipazione ai gol</span>
						<small>
							{contributionShare != null
								? `${display(contributionShare, 1)}% · `
								: ""}
							{contributionComplete
								? "Gol + assist"
								: `Dati parziali: ${contributionKnown.length}/${contributionGames.length} gare in distinta`}
						</small>
					</div>
				</Tooltip>
				{keys.filter(([key]) => key === "rating").map(metricTile)}
			</div>
			{!summaryOnly && (
				<p className="player-match-statistics__note">
					Il riepilogo considera le gare trascorse. «—» indica un dato mancante;
					0 è un valore registrato. I totali includono solo i dati disponibili.
				</p>
			)}
		</>
	);
	const contents = (
		<div
			className={
				"player-match-statistics" +
				(summaryOnly ? " player-match-statistics--compact" : "")
			}
			aria-busy={loading}
		>
			{!summaryOnly && (
				<div className="player-match-statistics__filters">
					<div>
						<VLFSelect
							label="Rosa"
							placeholder="Tutte le rose"
							value={roster}
							options={(data?.rosters || []).map((r) => ({
								value: r.id,
								label: r.name,
							}))}
							onChange={setRoster}
						/>
					</div>
					<div>
						<VLFSelect
							label="Competizione"
							placeholder="Tutte le competizioni"
							value={competition}
							options={(data?.competitions || []).map((c) => ({
								value: c.id,
								label: c.name,
							}))}
							onChange={setCompetition}
						/>
					</div>
					<div>
						<VLFDatePicker
							label="Dal"
							value={from}
							onChange={setFrom}
							clearable
						/>
					</div>
					<div>
						<VLFDatePicker label="Al" value={to} onChange={setTo} clearable />
					</div>
				</div>
			)}
			{invalidDates ? (
				<p role="alert">La data iniziale deve precedere quella finale.</p>
			) : loading ? (
				<p role="status">Caricamento statistiche delle partite…</p>
			) : error ? (
				<p role="alert">{error}</p>
			) : (
				summary
			)}
			{!summaryOnly && !loading && !error && !invalidDates && (
				<>
					<section
						className="player-match-statistics__employment"
						aria-label="Impiego nelle gare selezionate"
					>
						<h4>Impiego nelle gare selezionate</h4>
						<div>
							{employment.map((item) => (
								<p key={item.label}>
									<strong>
										{item.value}/{visibleMatches.length}
									</strong>
									<span>{item.label}</span>
								</p>
							))}
							<p>
								<strong>
									{display(minuteShare, 1)}
									{minuteShare != null ? "%" : ""}
								</strong>
								<span>Minuti disponibili giocati</span>
							</p>
						</div>
						{otherEmployment > 0 && (
							<small>
								{otherEmployment} gare con altro stato o impiego da chiarire.
							</small>
						)}
						<small>
							Stessa selezione del riepilogo. La percentuale usa i minuti
							rilevati; eventuali dati parziali sono indicati nel KPI Minuti.
						</small>
					</section>
					<ProfileCharts matches={visibleMatches} />
					<TechnicalDetails data={data} />
					<div className="player-match-statistics__list-title">
						Gare della stagione <span>{visibleMatches.length} gare</span>
					</div>
					<div className="player-match-statistics__table-wrapper">
						<table>
							<thead>
								<tr>
									<th>Data</th>
									<th>Partita</th>
									<th>Risultato</th>
									<th>Presenza</th>
									<th>Minuti</th>
									{data?.goalkeeper ? (
										<>
											<th>Subiti</th>
											<th>Parate</th>
										</>
									) : (
										<>
											<th>Gol</th>
											<th>Assist</th>
										</>
									)}
									<th>Cartellini</th>
									<th>Voto</th>
									<th>Dati</th>
									<th>
										<span className="visually-hidden">Dettaglio gara</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{visibleMatches.map((row) => (
									<Fragment key={row.matchId}>
										<tr className={row.upcoming ? "upcoming" : ""}>
											<td className="player-match-statistics__date">
												{moment(row.date).format("DD/MM/YYYY")}
												<small>
													{row.startTimeToBeConfirmed
														? "OdD"
														: moment(row.date).format("HH:mm")}
												</small>
											</td>
											<td className="player-match-statistics__fixture">
												<Link to={"/matches/" + row.matchId}>
													{row.matchSide && (
														<Tooltip
															title={
																row.matchSide === "home"
																	? "In casa"
																	: "In trasferta"
															}
														>
															{row.matchSide === "home" ? (
																<HomeIcon fontSize="small" />
															) : (
																<FlightIcon fontSize="small" />
															)}
														</Tooltip>
													)}
													<span>
														{row.homeTeam}
														<span className="player-match-statistics__vs">
															{" "}
															Vs{" "}
														</span>
														{row.awayTeam}
													</span>
												</Link>
												<small>
													{[row.competitionName, row.rosterName]
														.filter(Boolean)
														.join(" · ")}
												</small>
											</td>
											<td>
												{row.homeGoals != null && row.awayGoals != null
													? `${row.homeGoals}–${row.awayGoals}`
													: "—"}
											</td>
											<td>
												<span
													className={
														"player-match-statistics__status" +
														(row.inLineup ? " in-lineup" : "")
													}
												>
													{row.status}
												</span>
												{row.upcoming && row.status !== "Programmata" && (
													<small>Programmata</small>
												)}
											</td>
											<td>
												<Metric row={row} name="minutes" label="Minuti" />
											</td>
											{data?.goalkeeper ? (
												<>
													<td>
														<Metric
															row={row}
															name="goalsConceded"
															label="Gol subiti"
														/>
													</td>
													<td>
														<Metric row={row} name="saves" label="Parate" />
													</td>
												</>
											) : (
												<>
													<td>
														<Metric row={row} name="goals" label="Gol">
															<SportsSoccerIcon fontSize="inherit" />
														</Metric>
													</td>
													<td>
														<Metric row={row} name="assists" label="Assist">
															<HandshakeIcon fontSize="inherit" />
														</Metric>
													</td>
												</>
											)}
											<td>
												<div className="player-match-statistics__cards">
													<Metric row={row} name="yellowCards" label="Gialli">
														<i className="yellow-card" />
													</Metric>
													<Metric row={row} name="redCard" label="Rossi">
														<i className="red-card" />
													</Metric>
												</div>
											</td>
											<td>
												<Metric row={row} name="rating" label="Voto" />
											</td>
											<td>
												<Tooltip
													title={
														row.warning ||
														"Completezza dei dati registrati per la squadra del giocatore"
													}
												>
													<span
														className={
															"player-match-statistics__quality " + row.quality
														}
													>
														{qualityLabels[row.quality]}
													</span>
												</Tooltip>
											</td>
											<td>
												<Tooltip
													title={
														expandedMatch === row.matchId
															? "Chiudi dettaglio gara"
															: "Mostra dettaglio gara"
													}
												>
													<IconButton
														size="small"
														aria-label={
															expandedMatch === row.matchId
																? "Chiudi dettaglio gara"
																: "Mostra dettaglio gara"
														}
														aria-expanded={expandedMatch === row.matchId}
														onClick={(event) => {
															event.currentTarget.closest(
																".player-match-statistics__table-wrapper",
															).scrollLeft = 0;

															setExpandedMatch((value) =>
																value === row.matchId ? null : row.matchId,
															);
														}}
													>
														{expandedMatch === row.matchId ? (
															<ExpandLessIcon />
														) : (
															<ExpandMoreIcon />
														)}
													</IconButton>
												</Tooltip>
											</td>
										</tr>
										{expandedMatch === row.matchId && (
											<tr>
												<td colSpan={11}>
													<MatchDetails row={row} playerId={playerId} />
												</td>
											</tr>
										)}
									</Fragment>
								))}
							</tbody>
						</table>
					</div>
					{!visibleMatches.length && (
						<p>Nessuna gara per questa stagione e questi filtri.</p>
					)}
				</>
			)}
		</div>
	);
	return summaryOnly ? (
		contents
	) : (
		<VLFCard
			title={showTitle ? "Statistiche partite" : undefined}
			openable={false}
			content={contents}
		/>
	);
}
